import { Article } from '@wix/answers-api';
import { Box, IconButton, InputArea, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useRef, useState, KeyboardEvent } from 'react';

import { RelatedQuestionsSlider } from './RelatedQuestionsSlider';
import { SiteSelectorButton } from './SiteSelectorButton';
import { SiteSelectorModal } from './SiteSelectorModal';
import { CHATBOT_REFERRALS } from '../../../../constants';
import { SparklesIcon } from '../../../../icons/SparklesIcon';
import { useArticleUnifiedBi } from '../../../../utils/article-unified-bi';
import { useStore } from '../../../../zustand/stores';
import { useChatbotMessageSender } from '../../../ArticleRightSideBar/useChatbotMessageSender';

import css from './index.module.scss';

type PinnedChatInputProps = {
  article: Article;
  relatedQuestions?: string[];
  hidden?: boolean;
  hideRelatedQuestions?: boolean;
  alwaysFocused?: boolean;
  forceReferral?: string;
  onBeforeSend?: (text: string) => void | Promise<void>;
};

export const PinnedChatInput = ({
  article,
  relatedQuestions,
  hidden,
  hideRelatedQuestions,
  alwaysFocused = false,
  forceReferral,
  onBeforeSend,
}: PinnedChatInputProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isSiteSelectorModalOpen, setIsSiteSelectorModalOpen] = useState(false);
  const hasTrackedOpenRef = useRef(false);
  const { sendMessage } = useChatbotMessageSender({});
  const { setChatbotReferral } = useStore((state) => state.copilotSlice);
  const {
    inputFieldOpenTrack,
    inputFieldTextSubmitTrack,
    inputFieldSuggestedQuestionTrack,
  } = useArticleUnifiedBi(article);

  const handleFocus = () => {
    if (hasTrackedOpenRef.current) {
      return;
    }
    hasTrackedOpenRef.current = true;
    void inputFieldOpenTrack();
  };

  const handleSendMessage = async (message?: string) => {
    const text = message ?? query;
    if (!text.trim()) {
      return;
    }
    const referral = forceReferral ?? CHATBOT_REFERRALS.ARTICLE_INPUT_FIELD;
    void inputFieldTextSubmitTrack(text);
    setQuery('');
    setChatbotReferral(referral);
    await onBeforeSend?.(text);
    sendMessage(text, referral);
  };

  const handleSuggestedQuestionClick = async (
    question: string,
    index: number,
  ) => {
    if (!question.trim()) {
      return;
    }
    const referral =
      forceReferral ?? CHATBOT_REFERRALS.ARTICLE_SUGGESTED_QUESTION;
    void inputFieldSuggestedQuestionTrack(question, index + 1);
    setQuery('');
    setChatbotReferral(referral);
    await onBeforeSend?.(question);
    sendMessage(question, referral);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  const showRelatedQuestions =
    !hideRelatedQuestions &&
    !!relatedQuestions &&
    relatedQuestions.length > 0;

  return (
    <Box
      className={classNames(css.wrapper, {
        [css.hidden]: hidden,
        [css.fitContainer]: alwaysFocused,
      })}
      direction="vertical"
    >
      {showRelatedQuestions && (
        <RelatedQuestionsSlider
          relatedQuestions={relatedQuestions}
          onQuestionClick={handleSuggestedQuestionClick}
        />
      )}
      <div className={css.inputBox}>
        <div className={css.inputArea}>
          <SparklesIcon size={24} />
          <InputArea
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
            value={query}
            rows={2}
            className={css.inputAreaField}
            size="medium"
            placeholder={t('article-page.copilot.input.placeholder.ask-me-anything')}
            autoFocus={alwaysFocused}
          />
        </div>
        <div className={css.inputFooter}>
          <SiteSelectorButton
            article={article}
            onOpenPicker={() => setIsSiteSelectorModalOpen(true)}
          />
          <IconButton
            size="medium"
            className={css.sendButton}
            onClick={() => handleSendMessage()}
          >
            <ArrowRight />
          </IconButton>
        </div>
      </div>
      {alwaysFocused && (
        <Box className={css.disclaimer}>
          <Text size="tiny" weight="thin">
            {t('article-page.copilot.input.ai.disclaimer')}
          </Text>
        </Box>
      )}
      <SiteSelectorModal
        article={article}
        isOpen={isSiteSelectorModalOpen}
        onClose={() => setIsSiteSelectorModalOpen(false)}
        isLoadingSites={false}
      />
    </Box>
  );
};
